import { Button, Link, Message, Stack } from '@kolonialno/lama';
import { useEffect, useMemo, useRef } from 'react';
import { Link as ReactRouterLink, useSearchParams } from 'react-router';

import { useCSRFToken } from '../../utils/csrf';
import Container from '../components/Container';
import { useButtonLoading } from '../utils/button-state';
import { useAuthenticationContext } from '../utils/context';

function DeviceLoginNotAllowed() {
    return <Message variant="danger">Device login is not permitted.</Message>;
}

function Device() {
    const { disabled, loadingState, setLoading } = useButtonLoading();
    const loginButton = useRef<HTMLButtonElement>(null);

    const csrf = useCSRFToken();
    const context = useAuthenticationContext();
    const [searchParams] = useSearchParams();
    const targetDevice = searchParams.get('device');

    const targetIndex = useMemo(() => {
        if (targetDevice) {
            return context.deviceAccounts.findIndex(
                (account) => account.email === targetDevice,
            );
        }
        return 0;
    }, [context.deviceAccounts, targetDevice]);

    const autoLogin = useMemo(
        () =>
            context.deviceAccounts.length === 1 ||
            (targetDevice !== null && targetIndex !== -1),
        [context.deviceAccounts.length, targetDevice, targetIndex],
    );

    useEffect(() => {
        if (
            !disabled &&
            context.deviceAuthenticationEnabled &&
            context.deviceAuthenticationAllowed &&
            autoLogin &&
            loginButton.current
        ) {
            loginButton.current.click();
        }
    }, [
        disabled,
        loginButton,
        context.deviceAuthenticationEnabled,
        context.deviceAuthenticationAllowed,
        autoLogin,
    ]);

    return (
        <Container>
            {(!context.deviceAuthenticationEnabled ||
                !context.deviceAuthenticationAllowed) && (
                <DeviceLoginNotAllowed />
            )}

            {context.deviceAuthenticationEnabled &&
                context.deviceAuthenticationAllowed && (
                    <Stack direction="vertical">
                        {context.deviceAccounts.map((account, i) => (
                            <form
                                key={account.email}
                                method="post"
                                action="/authentication/login/"
                                onSubmit={() => {
                                    setLoading();
                                }}
                            >
                                <input
                                    type="hidden"
                                    name="csrfmiddlewaretoken"
                                    value={csrf}
                                />
                                <input
                                    type="hidden"
                                    name="rd"
                                    value={context.redirect || undefined}
                                />
                                <input
                                    type="hidden"
                                    name="username"
                                    value={account.email}
                                />
                                <input
                                    type="hidden"
                                    name="password"
                                    value={context.deviceAuthenticationPassword}
                                />
                                <Stack>
                                    <Button
                                        variant="primary"
                                        size="m"
                                        type="submit"
                                        ref={
                                            i === targetIndex
                                                ? loginButton
                                                : null
                                        }
                                        disabled={disabled}
                                        loadingState={loadingState}
                                    >
                                        {account.name}
                                    </Button>
                                </Stack>
                            </form>
                        ))}
                    </Stack>
                )}

            <Link
                as={ReactRouterLink}
                to={`/authentication/?rd=${context.redirect}`}
                className="purple-text"
            >
                Back
            </Link>
        </Container>
    );
}

export default Device;
